import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  cardStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
  Surface,
} from '@kilat/ui';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useIsCompact } from '../components/use-is-compact.js';
import { AppIconCard } from '../components/app-icon-card.js';
import { BookingDepositCard } from '../components/booking-deposit-card.js';
import { ContactLinksCard } from '../components/contact-links-card.js';
import { brandingRepoFor } from '../lib/repositories/branding-repo.js';
import { logger } from '../lib/logger.js';
import { computeHoursStatus, type Branding, type Hours, type HoursStatus } from '@kilat/shared';

/**
 * /branding/hours — per-day operating hours editor.
 *
 * Replaces the single weekly-window simplification from the Sprint 8 wizard
 * with a proper per-day editor + holiday overrides + temporary-closure
 * banner. Writes back into tenants/{tid}/branding/current.hours and bumps
 * brandingVersion so the customer-pwa picks up changes immediately
 * (manifest/icons cache uses brandingVersion in ETags).
 *
 * Premium-logical: explicit live "Today's status" header, copy-weekday-to-
 * weekend bulk action, and a holiday-override row that's add-as-you-need
 * (not a fixed table of 365 days). Every state spelt out per
 * feedback-no-dead-ends.
 */

const DAYS = [
  { key: 'mon' },
  { key: 'tue' },
  { key: 'wed' },
  { key: 'thu' },
  { key: 'fri' },
  { key: 'sat' },
  { key: 'sun' },
] as const;
type DayKey = (typeof DAYS)[number]['key'];

interface DayDraft {
  isOpen: boolean;
  open: string;
  close: string;
}

interface HolidayDraft {
  id: string; // local key for list-keying
  date: string;
  isOpen: boolean;
  open: string;
  close: string;
  label: string;
}

interface TemporaryClosureDraft {
  enabled: boolean;
  from: string;
  to: string;
  message: string;
}

export function BrandingHoursPage() {
  const { claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const [branding, setBranding] = useState<Branding | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [weekly, setWeekly] = useState<Record<DayKey, DayDraft>>(blankWeek());
  const [holidays, setHolidays] = useState<HolidayDraft[]>([]);
  const [tempClosure, setTempClosure] = useState<TemporaryClosureDraft>({
    enabled: false,
    from: '',
    to: '',
    message: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const { t } = useI18n();
  // Below 720px the rigid multi-column day / holiday rows stack to one column.
  const compact = useIsCompact(720);
  const [findListingBusy, setFindListingBusy] = useState(false);
  const [findListingError, setFindListingError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await brandingRepoFor(tenantId).get('current');
        if (cancelled) return;
        if (!doc) {
          setLoadError('No branding doc found for this tenant — finish onboarding first.');
          setLoaded(true);
          return;
        }
        setBranding(doc);
        hydrateFromBranding(doc.hours, setWeekly, setHolidays, setTempClosure);
        setLoaded(true);
      } catch (e) {
        logger.error('branding load failed', { error: String(e) });
        if (cancelled) return;
        setLoadError(String((e as Error).message ?? 'Could not load branding.'));
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const todayStatus = useMemo(
    () => computeHoursStatus(buildHoursPreview(weekly, holidays, tempClosure), new Date()),
    [weekly, holidays, tempClosure],
  );

  if (!tenantId) {
    return (
      <Screen
        title={t('branding.hours.signInTitle')}
        body={t('branding.hours.signInBody')}
        cta={{ label: t('branding.hours.backToQueue'), to: '/queue' }}
      />
    );
  }
  if (!loaded)
    return <Screen title={t('branding.hours.loadingTitle')} body={t('branding.hours.oneSecond')} />;
  if (loadError) {
    return (
      <Screen
        title={t('branding.hours.loadErrorTitle')}
        body={loadError}
        cta={{ label: t('branding.hours.backToQueue'), to: '/queue' }}
      />
    );
  }

  function setDay(day: DayKey, patch: Partial<DayDraft>) {
    setWeekly((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  }

  function copyWeekdayHoursToWeekend() {
    setWeekly((prev) => {
      const weekdayRef = prev.mon;
      return {
        ...prev,
        sat: { ...weekdayRef },
        sun: { ...weekdayRef },
      };
    });
  }

  function applyToAllOpenDays() {
    setWeekly((prev) => {
      const out: Record<DayKey, DayDraft> = { ...prev };
      for (const d of DAYS) {
        if (out[d.key].isOpen) {
          out[d.key] = { ...out[d.key], open: '09:00', close: '18:00' };
        }
      }
      return out;
    });
  }

  function addHoliday() {
    const today = new Date().toISOString().slice(0, 10);
    setHolidays((prev) => [
      ...prev,
      {
        id: `h-${Math.random().toString(36).slice(2, 8)}`,
        date: today,
        isOpen: false,
        open: '09:00',
        close: '18:00',
        label: '',
      },
    ]);
  }

  function updateHoliday(id: string, patch: Partial<HolidayDraft>) {
    setHolidays((prev) => prev.map((h) => (h.id === id ? { ...h, ...patch } : h)));
  }

  function removeHoliday(id: string) {
    setHolidays((prev) => prev.filter((h) => h.id !== id));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!tenantId || !branding) return;
    setSubmitError(null);
    setSavedAt(null);

    // Validation
    for (const d of DAYS) {
      const draft = weekly[d.key];
      const dayName = t(`find.detail.days.${d.key}`);
      if (draft.isOpen) {
        if (!isTimeStr(draft.open) || !isTimeStr(draft.close)) {
          setSubmitError(t('branding.hours.validation.dayInvalidTimes', { day: dayName }));
          return;
        }
        if (draft.open >= draft.close) {
          setSubmitError(t('branding.hours.validation.dayCloseAfterOpen', { day: dayName }));
          return;
        }
      }
    }
    for (const h of holidays) {
      if (!isDateStr(h.date)) {
        setSubmitError(
          t('branding.hours.validation.holidayDateFormat', { label: h.label || h.date }),
        );
        return;
      }
      if (h.isOpen && (!isTimeStr(h.open) || !isTimeStr(h.close) || h.open >= h.close)) {
        setSubmitError(t('branding.hours.validation.holidayTimeOrder', { date: h.date }));
        return;
      }
    }
    if (tempClosure.enabled) {
      if (!isDateStr(tempClosure.from) || !isDateStr(tempClosure.to)) {
        setSubmitError(t('branding.hours.validation.tempClosureDates'));
        return;
      }
      if (tempClosure.from > tempClosure.to) {
        setSubmitError(t('branding.hours.validation.tempClosureOrder'));
        return;
      }
    }

    setSubmitting(true);
    try {
      const nextHours: Hours = {
        weekly: Object.fromEntries(
          DAYS.map((d) => {
            const draft = weekly[d.key];
            return [
              d.key,
              draft.isOpen
                ? { isOpen: true, open: draft.open, close: draft.close }
                : { isOpen: false },
            ];
          }),
        ) as Hours['weekly'],
        holidayOverrides: holidays
          .slice()
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((h) => ({
            date: h.date,
            isOpen: h.isOpen,
            ...(h.isOpen ? { open: h.open, close: h.close } : {}),
            ...(h.label.trim() ? { label: h.label.trim() } : {}),
          })),
        ...(tempClosure.enabled
          ? {
              temporaryClosure: {
                from: tempClosure.from,
                to: tempClosure.to,
                messageI18n: { en: tempClosure.message.trim() || 'Temporarily closed' },
              },
            }
          : {}),
      };

      await brandingRepoFor(tenantId).update('current', {
        hours: nextHours,
        brandingVersion: (branding.brandingVersion ?? 0) + 1,
      });
      setSavedAt(Date.now());
      // Update local snapshot so a subsequent save has the right brandingVersion
      setBranding({
        ...branding,
        hours: nextHours,
        brandingVersion: (branding.brandingVersion ?? 0) + 1,
      });
    } catch (err) {
      logger.error('branding hours save failed', { error: String(err) });
      setSubmitError(String((err as Error).message ?? 'Could not save.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              letterSpacing: 4,
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {t('branding.hours.eyebrow')}
          </p>
          <h1
            style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}
          >
            {t('branding.hours.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t('branding.hours.subtitle')}
          </p>
        </div>
        <Link to="/queue" style={ghostLinkStyle}>
          {t('branding.hours.backToQueue')}
        </Link>
      </header>

      <TodayStatusCard status={todayStatus} />

      {/*
        The app icon. Outside the hours <form> on purpose: it applies
        immediately through its own callable rather than waiting for the form's
        Save, because uploading a logo and setting opening hours are unrelated
        actions and batching them would make one wait on the other.
      */}
      {tenantId !== null && branding !== null ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <AppIconCard
            tenantId={tenantId}
            iconSrc={branding.iconSet.find((i) => i.purpose === 'any')?.src ?? null}
            brandingVersion={branding.brandingVersion}
          />
        </div>
      ) : null}

      {/* Links the customer app shows — and the Google review link it offers
          after every rating (Share & grow). Saved on its own, like the icon. */}
      {tenantId !== null && branding !== null ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <ContactLinksCard
            tenantId={tenantId}
            contact={branding.contact}
            onSaved={(contact) => setBranding({ ...branding, contact })}
          />
        </div>
      ) : null}

      {/*
        Booking deposits live on this screen because they belong to the same
        decision as the hours above it: both govern what happens when a customer
        books a slot rather than walking up. Splitting them across two routes
        would make an operator hunt for the half they were not thinking about.
      */}
      {tenantId !== null ? <BookingDepositCard tenantId={tenantId} /> : null}

      <form
        onSubmit={submit}
        style={{
          display: 'grid',
          gap: 'var(--space-4)',
          marginTop: 'var(--space-6)',
        }}
      >
        <Surface as="section">
          <header
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
              {t('branding.hours.weeklyHeading')}
            </h2>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={copyWeekdayHoursToWeekend}
                style={miniGhostButtonStyle}
                title={t('branding.hours.copyWeekdayTooltip')}
              >
                {t('branding.hours.copyWeekdayToWeekend')}
              </button>
              <button
                type="button"
                onClick={applyToAllOpenDays}
                style={miniGhostButtonStyle}
                title={t('branding.hours.applyNineSixTooltip')}
              >
                {t('branding.hours.applyNineSix')}
              </button>
            </div>
          </header>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {DAYS.map((d) => (
              <DayRow
                key={d.key}
                label={t(`find.detail.days.${d.key}`)}
                draft={weekly[d.key]}
                compact={compact}
                onChange={(patch) => setDay(d.key, patch)}
              />
            ))}
          </div>
        </Surface>

        <Surface as="section">
          <header
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
                {t('branding.hours.holidaysHeading')}
              </h2>
              <p
                style={{
                  margin: 'var(--space-1) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {t('branding.hours.holidaysDescription')}
              </p>
            </div>
            <button type="button" onClick={addHoliday} style={miniGhostButtonStyle}>
              {t('branding.hours.addHoliday')}
            </button>
          </header>
          {holidays.length === 0 ? (
            <p
              style={{
                margin: 'var(--space-4) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                textAlign: 'center',
              }}
            >
              {t('branding.hours.holidaysEmpty')}
            </p>
          ) : (
            <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
              {holidays.map((h) => (
                <HolidayRow
                  key={h.id}
                  draft={h}
                  compact={compact}
                  onChange={(patch) => updateHoliday(h.id, patch)}
                  onRemove={() => removeHoliday(h.id)}
                />
              ))}
            </div>
          )}
        </Surface>

        <Surface as="section">
          <header>
            <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
              {t('branding.hours.tempClosureHeading')}
            </h2>
            <p
              style={{
                margin: 'var(--space-1) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t('branding.hours.tempClosureDescription')}
            </p>
          </header>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-3)',
            }}
          >
            <input
              type="checkbox"
              checked={tempClosure.enabled}
              onChange={(e) => setTempClosure((prev) => ({ ...prev, enabled: e.target.checked }))}
              style={{ width: 18, height: 18 }}
            />
            <span style={fieldLabel}>{t('branding.hours.tempClosureToggle')}</span>
          </label>
          {tempClosure.enabled ? (
            <div
              style={{
                marginTop: 'var(--space-3)',
                display: 'grid',
                gap: 'var(--space-3)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
              }}
            >
              <label style={fieldStack}>
                <span style={fieldLabel}>{t('branding.hours.from')}</span>
                <input
                  type="date"
                  value={tempClosure.from}
                  onChange={(e) => setTempClosure((prev) => ({ ...prev, from: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={fieldStack}>
                <span style={fieldLabel}>{t('branding.hours.to')}</span>
                <input
                  type="date"
                  value={tempClosure.to}
                  onChange={(e) => setTempClosure((prev) => ({ ...prev, to: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={{ ...fieldStack, gridColumn: '1 / -1' }}>
                <span style={fieldLabel}>{t('branding.hours.messageLabel')}</span>
                <input
                  type="text"
                  value={tempClosure.message}
                  onChange={(e) => setTempClosure((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder={t('branding.hours.messagePlaceholder')}
                  style={inputStyle}
                />
              </label>
            </div>
          ) : null}
        </Surface>

        {submitError ? (
          <p
            role="alert"
            style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
          >
            {submitError}
          </p>
        ) : null}
        {savedAt ? (
          <p
            role="status"
            style={{ margin: 0, color: 'var(--color-primary)', fontSize: 'var(--text-meta)' }}
          >
            {t('branding.hours.savedNote')}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              ...primaryButtonStyle,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? t('dashboard.hours.saving') : t('dashboard.hours.saveHours')}
          </button>
        </div>
      </form>

      {branding ? (
        <section
          style={{ ...cardStyle, marginTop: 'var(--space-4)', marginBottom: 'var(--space-16)' }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-4)',
            }}
          >
            <header style={{ flex: '1 1 280px' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
                {t('branding.findListing.title')}
              </h2>
              <p
                style={{
                  margin: 'var(--space-1) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {t('branding.findListing.description')}
              </p>
            </header>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-1)',
              }}
            >
              <button
                type="button"
                role="switch"
                aria-checked={branding.listedInFindDirectory}
                aria-label={t('branding.findListing.title')}
                disabled={findListingBusy}
                onClick={() => void toggleFindListing(!branding.listedInFindDirectory)}
                style={{
                  width: 48,
                  height: 28,
                  borderRadius: 999,
                  border: 'none',
                  padding: 3,
                  cursor: findListingBusy ? 'wait' : 'pointer',
                  background: branding.listedInFindDirectory
                    ? 'var(--color-primary)'
                    : 'rgba(15,23,42,0.22)',
                  display: 'inline-flex',
                  justifyContent: branding.listedInFindDirectory ? 'flex-end' : 'flex-start',
                  alignItems: 'center',
                  transition: 'background 160ms ease',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.3)',
                  }}
                />
              </button>
              <span
                style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
              >
                {findListingBusy
                  ? t('branding.findListing.saving')
                  : branding.listedInFindDirectory
                    ? t('branding.findListing.on')
                    : t('branding.findListing.off')}
              </span>
            </div>
          </div>
          {findListingError ? (
            <p
              role="alert"
              style={{
                margin: 'var(--space-2) 0 0 0',
                color: 'var(--color-accent)',
                fontSize: 'var(--text-meta)',
              }}
            >
              {findListingError}
            </p>
          ) : null}
        </section>
      ) : null}
    </main>
  );

  async function toggleFindListing(next: boolean): Promise<void> {
    if (!tenantId || !branding || findListingBusy) return;
    setFindListingBusy(true);
    setFindListingError(null);
    const nextVersion = (branding.brandingVersion ?? 0) + 1;
    try {
      await brandingRepoFor(tenantId).update('current', {
        listedInFindDirectory: next,
        brandingVersion: nextVersion,
      });
      setBranding({ ...branding, listedInFindDirectory: next, brandingVersion: nextVersion });
    } catch (e) {
      logger.error('find listing toggle failed', { tenantId, error: String(e) });
      setFindListingError(t('branding.findListing.error'));
    } finally {
      setFindListingBusy(false);
    }
  }
}

// ── Today status ──────────────────────────────────────────────────────

function TodayStatusCard({ status }: { readonly status: HoursStatus }) {
  const { t } = useI18n();
  let pill: { bg: string; fg: string; label: string };
  let body: string;
  switch (status.kind) {
    case 'temp_closed':
      pill = {
        bg: 'rgba(239,68,68,0.10)',
        fg: 'var(--color-accent)',
        label: t('branding.hours.status.tempClosedLabel'),
      };
      body = status.until
        ? t('branding.hours.status.tempClosedBodyUntil', {
            message: status.message,
            until: status.until,
          })
        : status.message;
      break;
    case 'holiday_closed':
      pill = {
        bg: 'rgba(239,68,68,0.10)',
        fg: 'var(--color-accent)',
        label: t('branding.hours.status.holidayClosedLabel'),
      };
      body =
        status.label ?? t('branding.hours.status.holidayClosedBodyFallback', { date: status.date });
      break;
    case 'holiday_open':
      pill = {
        bg: 'rgba(245,158,11,0.10)',
        fg: '#b45309',
        label: t('branding.hours.status.holidayOpenLabel'),
      };
      body = t('branding.hours.status.holidayOpenBody', {
        name: status.label ?? t('branding.hours.status.holidayFallback', { date: status.date }),
        until: status.until,
      });
      break;
    case 'closed':
      pill = {
        bg: 'var(--color-border)',
        fg: 'var(--color-on-surface-muted)',
        label: t('branding.hours.status.closedLabel'),
      };
      body = t('branding.hours.status.closedBody');
      break;
    case 'open':
      pill = {
        bg: 'rgba(13,122,79,0.10)',
        fg: 'var(--color-primary)',
        label: t('branding.hours.status.openLabel'),
      };
      body = t('branding.hours.status.openBody', { until: status.until });
      break;
    case 'open_24h':
      pill = {
        bg: 'rgba(13,122,79,0.10)',
        fg: 'var(--color-primary)',
        label: t('branding.hours.status.open24hLabel'),
      };
      body = t('branding.hours.status.open24hBody');
      break;
    case 'opens_later':
      pill = {
        bg: 'rgba(245,158,11,0.10)',
        fg: '#b45309',
        label: t('branding.hours.status.opensLaterLabel'),
      };
      body = t('branding.hours.status.opensLaterBody', {
        opensAt: status.opensAt,
        closesAt: status.closesAt,
      });
      break;
  }
  return (
    <section
      style={{
        ...cardStyle,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          padding: '4px 12px',
          borderRadius: 'var(--radius-pill)',
          background: pill.bg,
          color: pill.fg,
          fontSize: 'var(--text-meta)',
          fontWeight: 500,
        }}
      >
        {pill.label}
      </span>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {body}
      </span>
    </section>
  );
}

// ── rows ──────────────────────────────────────────────────────────────

function DayRow({
  label,
  draft,
  compact,
  onChange,
}: {
  readonly label: string;
  readonly draft: DayDraft;
  readonly compact?: boolean;
  readonly onChange: (patch: Partial<DayDraft>) => void;
}) {
  const { t } = useI18n();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : '120px auto 1fr 1fr',
        gap: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: draft.isOpen ? 'var(--color-surface)' : 'var(--tint-subtle)',
        alignItems: compact ? 'stretch' : 'center',
      }}
    >
      <span style={{ fontWeight: 500 }}>{label}</span>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={draft.isOpen}
          onChange={(e) => onChange({ isOpen: e.target.checked })}
          style={{ width: 16, height: 16 }}
        />
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {draft.isOpen ? t('branding.hours.open') : t('branding.hours.closed')}
        </span>
      </label>
      <LabeledTime
        show={compact}
        labelText={t('branding.hours.from')}
        value={draft.open}
        disabled={!draft.isOpen}
        onChange={(v) => onChange({ open: v })}
      />
      <LabeledTime
        show={compact}
        labelText={t('branding.hours.to')}
        value={draft.close}
        disabled={!draft.isOpen}
        onChange={(v) => onChange({ close: v })}
      />
    </div>
  );
}

function HolidayRow({
  draft,
  compact,
  onChange,
  onRemove,
}: {
  readonly draft: HolidayDraft;
  readonly compact?: boolean;
  readonly onChange: (patch: Partial<HolidayDraft>) => void;
  readonly onRemove: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : '160px 110px 1fr 1fr 1fr auto',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        alignItems: compact ? 'stretch' : 'center',
      }}
    >
      <input
        type="date"
        value={draft.date}
        onChange={(e) => onChange({ date: e.target.value })}
        style={{ ...inputStyle, width: '100%' }}
      />
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={draft.isOpen}
          onChange={(e) => onChange({ isOpen: e.target.checked })}
          style={{ width: 16, height: 16 }}
        />
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {draft.isOpen ? t('branding.hours.open') : t('branding.hours.closed')}
        </span>
      </label>
      <LabeledTime
        show={compact}
        labelText={t('branding.hours.from')}
        value={draft.open}
        disabled={!draft.isOpen}
        onChange={(v) => onChange({ open: v })}
      />
      <LabeledTime
        show={compact}
        labelText={t('branding.hours.to')}
        value={draft.close}
        disabled={!draft.isOpen}
        onChange={(v) => onChange({ close: v })}
      />
      <input
        type="text"
        value={draft.label}
        onChange={(e) => onChange({ label: e.target.value })}
        placeholder={t('branding.hours.holidayLabelPlaceholder')}
        style={{ ...inputStyle, width: '100%' }}
      />
      <Button variant="danger" onClick={onRemove} title={t('branding.hours.removeHoliday')}>
        {t('branding.hours.removeHoliday')}
      </Button>
    </div>
  );
}

/**
 * A `<input type="time">` that, on narrow screens (`show`), is prefixed with a
 * visible From/To label so a stacked single-column row stays legible. On wide
 * screens it renders as the bare input the desktop grid columns expect.
 */
function LabeledTime({
  show,
  labelText,
  value,
  disabled,
  onChange,
}: {
  readonly show?: boolean;
  readonly labelText: string;
  readonly value: string;
  readonly disabled?: boolean;
  readonly onChange: (v: string) => void;
}) {
  const input = (
    <input
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{ ...inputStyle, width: '100%', opacity: disabled ? 0.5 : 1 }}
    />
  );
  if (!show) return input;
  return (
    <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {labelText}
      </span>
      {input}
    </label>
  );
}

// ── helpers ───────────────────────────────────────────────────────────

function blankWeek(): Record<DayKey, DayDraft> {
  const init: Partial<Record<DayKey, DayDraft>> = {};
  for (const d of DAYS) {
    init[d.key] = { isOpen: true, open: '09:00', close: '18:00' };
  }
  return init as Record<DayKey, DayDraft>;
}

function hydrateFromBranding(
  hours: Hours,
  setWeekly: (next: Record<DayKey, DayDraft>) => void,
  setHolidays: (next: HolidayDraft[]) => void,
  setTemp: (next: TemporaryClosureDraft) => void,
) {
  const next: Record<DayKey, DayDraft> = blankWeek();
  for (const d of DAYS) {
    const v = hours.weekly[d.key];
    next[d.key] = {
      isOpen: !!v?.isOpen,
      open: v?.open ?? '09:00',
      close: v?.close ?? '18:00',
    };
  }
  setWeekly(next);

  setHolidays(
    (hours.holidayOverrides ?? []).map((h, i) => ({
      id: `h-${i}-${h.date}`,
      date: h.date,
      isOpen: h.isOpen,
      open: h.open ?? '09:00',
      close: h.close ?? '18:00',
      label: h.label ?? '',
    })),
  );

  if (hours.temporaryClosure) {
    setTemp({
      enabled: true,
      from: hours.temporaryClosure.from,
      to: hours.temporaryClosure.to,
      message: hours.temporaryClosure.messageI18n['en'] ?? '',
    });
  } else {
    setTemp({ enabled: false, from: '', to: '', message: '' });
  }
}

function isTimeStr(s: string): boolean {
  return /^\d{2}:\d{2}$/.test(s);
}

function isDateStr(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/**
 * Convert the in-flight form drafts into the canonical Hours shape so the
 * shared `computeHoursStatus` can render the preview against what the
 * operator is about to save (not the persisted doc).
 */
function buildHoursPreview(
  weekly: Record<DayKey, DayDraft>,
  holidays: HolidayDraft[],
  temp: TemporaryClosureDraft,
): Hours {
  return {
    weekly: Object.fromEntries(
      DAYS.map((d) => {
        const draft = weekly[d.key];
        return [
          d.key,
          draft.isOpen ? { isOpen: true, open: draft.open, close: draft.close } : { isOpen: false },
        ];
      }),
    ) as Hours['weekly'],
    holidayOverrides: holidays.map((h) => ({
      date: h.date,
      isOpen: h.isOpen,
      ...(h.isOpen ? { open: h.open, close: h.close } : {}),
      ...(h.label.trim() ? { label: h.label.trim() } : {}),
    })),
    ...(temp.enabled && isDateStr(temp.from) && isDateStr(temp.to)
      ? {
          temporaryClosure: {
            from: temp.from,
            to: temp.to,
            messageI18n: { en: temp.message || 'Temporarily closed' },
          },
        }
      : {}),
  };
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 360 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldStack: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)' };

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};
